import React from "react";
import { Bar } from "react-chartjs-2";
import { Box, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from "chart.js";

import { round } from "utils";

// chart.js v3 is tree-shakeable — register the bar pieces we use (idempotent).
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const CARD_SX = {
  bgcolor: "shades.clr-white-900",
  border: "1px solid",
  borderColor: "neutral.clr-100",
  borderRadius: 3,
  p: 5,
};

/** Loading placeholder — same card footprint so the page doesn't jump. */
export function AttemptsComparisonSkeleton() {
  return (
    <Box sx={CARD_SX}>
      <Skeleton variant="text" width="65%" height={26} />
      <Skeleton variant="text" width="45%" height={18} sx={{ mb: 4 }} />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={200}
        sx={{ borderRadius: 2 }}
      />
    </Box>
  );
}

function LegendDot({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{ width: 14, height: 14, borderRadius: "4px", bgcolor: color }}
      />
      <Typography variant="body02-semiBold" color="neutral.clr-700">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Attempts performance comparison (mobile) — mobile port of desktop
 * `components/AttemptGraph` (shown by `PerformanceReview/GraphAnalysis` once a
 * template has 2+ attempts, replacing the "take one more attempt" card).
 *
 * Grouped bar chart: Speech + Content per attempt, with the attempt currently
 * being viewed highlighted (saturated) and the others dimmed — same data model
 * as desktop. Like desktop it shows the current attempt plus up to 3 older ones
 * (oldest → newest, left → right).
 *
 * @param {Array}  allAttempts       finished attempts, newest-first (from store)
 * @param {string} currentAttemptId  the attempt being viewed
 */
export default function AttemptsComparison({
  allAttempts = [],
  currentAttemptId,
}) {
  const theme = useTheme();

  // Speech = purple (tertiary), Content = teal (secondary). Current attempt uses
  // the saturated tone; other attempts a lighter tint of the same hue.
  const speechCurrent = theme.palette.tertiary["clr-500"];
  const speechOther = theme.palette.tertiary["clr-300"];
  const contentCurrent = theme.palette.secondary["clr-700"];
  const contentOther = theme.palette.secondary["clr-500"];

  // Match desktop: current attempt + up to 3 older ones (allAttempts is
  // newest-first), then reverse so it reads oldest → newest.
  const attempts = React.useMemo(() => {
    const idx = allAttempts.findIndex((a) => a._id === currentAttemptId);
    const start = idx < 0 ? 0 : idx;
    return [...allAttempts.slice(start, start + 4)].reverse();
  }, [allAttempts, currentAttemptId]);

  const labels = attempts.map((a) => a.attemptNumber);

  const data = React.useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "Speech",
          data: attempts.map((a) => round(a.communication)),
          backgroundColor: attempts.map((a) =>
            a._id === currentAttemptId ? speechCurrent : speechOther
          ),
          borderRadius: 4,
          borderSkipped: false,
        },
        {
          label: "Content",
          data: attempts.map((a) => round(a.content)),
          backgroundColor: attempts.map((a) =>
            a._id === currentAttemptId ? contentCurrent : contentOther
          ),
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attempts, currentAttemptId]
  );

  const options = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => `Attempt No. ${items?.[0]?.label ?? ""}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { stepSize: 25, color: theme.palette.neutral["clr-500"] },
          title: {
            display: true,
            text: "Score",
            color: theme.palette.neutral["clr-600"],
          },
          grid: { color: theme.palette.neutral["clr-200"] },
        },
        x: {
          ticks: { color: theme.palette.neutral["clr-500"] },
          title: {
            display: true,
            text: "Attempt No.",
            color: theme.palette.neutral["clr-600"],
          },
          grid: { display: false },
        },
      },
    }),
    [theme]
  );

  return (
    <Box sx={CARD_SX}>
      <Typography variant="h6-semiBold" color="shades.clr-black-900">
        Attempts performance comparison
      </Typography>
      <Typography variant="body02-regular" color="neutral.clr-500">
        Compare your attempts &amp; track progress
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 4, mt: 3 }}>
        <LegendDot color={speechCurrent} label="Speech" />
        <LegendDot color={contentCurrent} label="Content" />
      </Box>

      <Box sx={{ position: "relative", width: "100%", height: 220, mt: 3 }}>
        <Bar options={options} data={data} />
      </Box>
    </Box>
  );
}
